import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useDraftSettlement, useExitPreview, useLifecycleBoard } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

/** Exits, and what each one still owes (HRX-06).
 *
 * `Full and Final Statement` held zero rows while two people had already left, neither with a reason for
 * leaving recorded. The checklist only became answerable last week: waves M and N made the balances real, so
 * each block below is actual money or actual company property rather than a box somebody ticks.
 *
 * Two things it will not do. Assets in a leaver's hands are listed to be **handed back**, not charged into the
 * settlement — pricing a truck a driver returns on Friday as a 32 M CDF debt on Thursday is worse than useless.
 * And gratuity, severance and pay in lieu of notice are absent: they are statutory payroll calculations held to
 * WP-16, and an estimate here would be read as a figure. */
export default function Exits({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useLifecycleBoard(company || undefined);
  const [open, setOpen] = useState<string | null>(null);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-hr-exits>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-hr-exits-strip>
        <Stat label={t("evt.exits")} value={d.summary.leavers} hint={t("evt.exitsHint", { n: d.summary.mid_exit })} />
        <Stat label={t("evt.notReady")} value={d.summary.not_ready}
          cls={d.summary.not_ready ? "text-status-amber" : "text-status-green"} hint={t("evt.notReadyHint")} />
        <Stat label={t("evt.settlements")} value={d.summary.settlements} hint={t("evt.settlementsHint")} />
        <Stat label={t("evt.advanceAtRisk")} value={<Money value={d.summary.advance_at_risk} lng={lng} />}
          cls={d.summary.advance_at_risk ? "text-status-red" : ""} hint={t("evt.advanceAtRiskHint")} />
        <Stat label={t("evt.assetsHeld")} value={`${d.assets.with_custodian}/${d.assets.total}`}
          hint={t("evt.assetsHeldHint")} />
      </div>

      <SectionCard title={t("evt.exitQueue")} aside={t("evt.exitQueueN", { n: d.exits.length })}>
        {d.exits.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-hr-exits-empty>{t("evt.noExits")}</div>
        ) : (
          <ul>
            {d.exits.map((e) => (
              <li key={e.name} className="border-t border-brand-grey-light first:border-t-0" data-hr-exit={e.name}>
                <button onClick={() => setOpen(open === e.name ? null : e.name)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F6F8FA]"
                  data-hr-exit-open={e.name}>
                  <div className="min-w-0">
                    <div className="font-medium">
                      {e.employee_name}
                      {e.mid_exit && <span className="ml-2 rounded bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-brand-primary">{t("evt.midExit")}</span>}
                    </div>
                    <div className="text-[11px] text-brand-grey">
                      {e.designation} · {e.company} · {t("evt.served", { years: e.service.years, months: e.service.months })}
                      {e.relieving_date ? ` · ${t("evt.until", { date: e.relieving_date })}` : ""}
                    </div>
                    {e.reason_for_leaving && <div className="mt-0.5 max-w-2xl text-[11px] italic text-brand-grey">{e.reason_for_leaving}</div>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {e.readiness.ready ? (
                      <span className="rounded bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-status-green">{t("evt.clean")}</span>
                    ) : e.readiness.blocks.map((b) => (
                      <span key={b.key} className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-status-amber"
                        data-hr-exit-block={b.key}>
                        {t(`evt.block.${b.key}`, { count: b.count ?? 0, amount: b.amount ?? 0 })}
                      </span>
                    ))}
                  </div>
                </button>
                {open === e.name && <Preview employee={e.name} lng={lng} />}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function Preview({ employee, lng }: { employee: string; lng: string }) {
  const { t } = useTranslation();
  const q = useExitPreview(employee);
  const draft = useDraftSettlement();

  if (q.isError) return <div className="p-4"><ErrorBox error={q.error} /></div>;
  if (!q.data) return <Skeleton className="m-4 h-32" />;
  const d = q.data;

  return (
    <div className="border-t border-brand-grey-light bg-[#F6F8FA] p-4" data-hr-exit-preview={employee}>
      <div className="grid gap-4 md:grid-cols-3">
        <Column title={t("evt.owedToThem")}>
          {d.payables.length === 0 ? <Empty text={t("evt.nothing")} /> : d.payables.map((p) => (
            <Line key={p.key} label={t(`evt.line.${p.key}`)} detail={p.detail} value={<Money value={p.amount} lng={lng} />} />
          ))}
        </Column>
        <Column title={t("evt.owedByThem")}>
          {d.receivables.length === 0 ? <Empty text={t("evt.nothing")} /> : d.receivables.map((r) => (
            <Line key={r.key} label={t(`evt.line.${r.key}`)} detail={r.detail} value={<Money value={r.amount} lng={lng} />} />
          ))}
        </Column>
        <Column title={t("evt.toHandBack")}>
          {d.assets.count === 0 ? <Empty text={t("evt.nothing")} /> : d.assets.items.map((a) => (
            <Line key={a.name ?? ""} label={a.asset_name ?? a.name ?? ""} detail={a.category}
              value={<Money value={a.value} lng={lng} />} />
          ))}
          {d.assets.count > 0 && <p className="mt-1 text-[11px] text-brand-grey">{t("evt.assetsNotCharged")}</p>}
        </Column>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-brand-grey-light pt-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`evt.direction.${d.settlement.direction}`)}</div>
          <div className="font-heading text-xl font-semibold" data-hr-exit-net>
            <Money value={d.settlement.net} lng={lng} />
          </div>
          <p className="mt-1 max-w-2xl text-[11px] text-brand-grey">
            {t("evt.excluded", { list: d.excluded.map((x) => t(`evt.excludedItem.${x}`)).join(", ") })}
          </p>
        </div>
        {d.can_record && !d.existing && (
          <button onClick={() => draft.mutate({ employee })} disabled={draft.isPending}
            data-hr-exit-draft={employee}
            className="rounded bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {t("evt.draftSettlement")}
          </button>
        )}
        {d.existing && (
          <span className="rounded bg-green-50 px-3 py-1 text-xs font-semibold text-status-green" data-hr-exit-statement>
            {t("evt.statementExists", { name: d.existing.name, status: d.existing.status ?? "" })}
          </span>
        )}
      </div>
      {draft.isError && <div className="mt-2"><ErrorBox error={draft.error} /></div>}
    </div>
  );
}

function Column({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{title}</div>
      <div className="mt-1 space-y-1">{children}</div>
    </div>
  );
}

function Line({ label, detail, value }: { label: string; detail?: string | null; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span>{label}{detail ? <span className="ml-1 text-[11px] text-brand-grey">({detail})</span> : null}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

const Empty = ({ text }: { text: string }) => <div className="text-sm text-brand-grey">{text}</div>;
